import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Download, Volume2, VolumeX } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
};

export default function AudioPlayer({ src, title, subtitle }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => { setDuration(a.duration || 0); setLoading(false); };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("canplay", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("canplay", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = async () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { await a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (ref.current && duration) {
      ref.current.currentTime = pct * duration;
      setTime(pct * duration);
    }
  };

  const progress = duration > 0 ? (time / duration) * 100 : 0;

  return (
    <div className="relative grain bg-cream-100 border border-sage-300/60 rounded-sm overflow-hidden" data-testid="audio-player">
      <div className="p-6 sm:p-8 flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-sage-700 mb-2">Your Mind Magic Audio</p>
            <h3 className="font-display text-3xl sm:text-4xl text-ink leading-tight" data-testid="audio-title">{title}</h3>
            {subtitle && <p className="font-sans text-sm text-ink/60 mt-2">{subtitle}</p>}
          </div>
          <a
            href={src}
            download
            className="hidden sm:inline-flex items-center gap-2 font-sans text-xs tracking-widest uppercase text-ink/60 hover:text-sage-700 link-reveal"
            data-testid="audio-download"
          >
            <Download className="h-3.5 w-3.5" /> Save to device
          </a>
        </div>

        <div className="flex items-center gap-5">
          <button
            type="button"
            onClick={toggle}
            disabled={loading}
            className="group relative h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-ink text-cream-100 flex items-center justify-center hover:bg-sage-700 transition-colors disabled:opacity-50"
            data-testid="audio-play-toggle"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
            <span className={`absolute inset-0 rounded-full border border-sage-500/60 ${playing ? "animate-ping" : ""}`}></span>
          </button>

          <div className="flex-1">
            <div
              className="relative h-9 cursor-pointer flex items-center"
              onClick={seek}
              data-testid="audio-scrubber"
            >
              {/* waveform bars */}
              <div className="absolute inset-0 flex items-center gap-[3px] overflow-hidden px-0.5">
                {Array.from({ length: 56 }).map((_, i) => {
                  const h = 18 + ((i * 53) % 22);
                  const passed = (i / 56) * 100 < progress;
                  return (
                    <span
                      key={i}
                      className={`flex-1 rounded-sm ${passed ? "bg-sage-700" : "bg-sage-300/70"} ${playing && passed ? "animate-wave" : ""}`}
                      style={{ height: `${h}px`, animationDelay: `${i * 30}ms` }}
                    />
                  );
                })}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between font-sans text-xs text-ink/55 tabular-nums">
              <span data-testid="audio-current-time">{fmt(time)}</span>
              <span data-testid="audio-duration">{fmt(duration)}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => { if (ref.current) { ref.current.muted = !ref.current.muted; setMuted(ref.current.muted); } }}
            className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full text-ink/60 hover:text-sage-700"
            data-testid="audio-mute-toggle"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <audio ref={ref} src={src} preload="metadata" />
    </div>
  );
}
